/**
 * User ID utility for tracking unique users
 * - For authenticated users: Uses their Supabase user ID
 * - For anonymous users: Uses localStorage to persist a unique user ID across sessions
 *
 * Note: User ID is essential for enforcing itinerary limits (legitimate interest under GDPR)
 * Consent is checked but essential cookies are always allowed
 */

import { hasConsent } from '../components/common/CookieConsent';

const USER_ID_KEY = 'safarglimpse_user_id';
const USER_ID_EXPIRY_DAYS = 730; // User ID expires after 2 years (updated per GDPR retention policy)

// Import auth token to check if user is logged in
function getAuthenticatedUserId() {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;

    // Parse JWT to get user ID
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub; // Supabase user ID is in the 'sub' claim
  } catch (e) {
    return null;
  }
}

/**
 * Generate a unique user ID
 */
function generateUserId() {
  // Generate a unique ID using timestamp + random string
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const random2 = Math.random().toString(36).substring(2, 15);
  return `user_${timestamp}_${random}${random2}`;
}

/**
 * Get or create a unique user ID
 * - For authenticated users: Returns their Supabase user ID
 * - For anonymous users: Generates/retrieves anonymous ID from localStorage
 *
 * Note: Essential for service functionality (itinerary limits), so always allowed
 * but we check consent for transparency
 */
export function getUserId() {
  // Check if user is authenticated - if so, use their authenticated ID
  const authenticatedUserId = getAuthenticatedUserId();
  if (authenticatedUserId) {
    return authenticatedUserId;
  }

  // User not authenticated - use anonymous tracking ID
  // Check consent (essential is always true, but check for transparency)
  if (!hasConsent('essential')) {
    // Even without explicit consent, essential cookies are allowed for legitimate interest
    // But we log this for transparency
    console.debug('User ID: Essential cookie - allowed for legitimate interest (itinerary limits)');
  }

  try {
    // Check if user ID exists in localStorage
    const stored = localStorage.getItem(USER_ID_KEY);
    
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const { userId, createdAt } = data;
        
        // Check if user ID has expired (older than retention period)
        const expiryDate = new Date(createdAt);
        expiryDate.setDate(expiryDate.getDate() + USER_ID_EXPIRY_DAYS);
        
        if (new Date() > expiryDate) {
          // User ID expired, generate new one
          const newUserId = generateUserId();
          localStorage.setItem(USER_ID_KEY, JSON.stringify({
            userId: newUserId,
            createdAt: new Date().toISOString()
          }));
          return newUserId;
        }
        
        return userId;
      } catch (e) {
        // Invalid stored data, generate new ID
        const newUserId = generateUserId();
        localStorage.setItem(USER_ID_KEY, JSON.stringify({
          userId: newUserId,
          createdAt: new Date().toISOString()
        }));
        return newUserId;
      }
    }
    
    // No user ID found, generate new one
    const newUserId = generateUserId();
    localStorage.setItem(USER_ID_KEY, JSON.stringify({
      userId: newUserId,
      createdAt: new Date().toISOString()
    }));
    return newUserId;
  } catch (e) {
    // localStorage not available (e.g., private browsing), use session-based ID
    console.warn('localStorage not available, using session-based ID');
    if (!sessionStorage.getItem(USER_ID_KEY)) {
      const sessionId = generateUserId();
      sessionStorage.setItem(USER_ID_KEY, sessionId);
      return sessionId;
    }
    return sessionStorage.getItem(USER_ID_KEY);
  }
}

/**
 * Clear user ID (for testing or reset purposes)
 */
export function clearUserId() {
  try {
    localStorage.removeItem(USER_ID_KEY);
    sessionStorage.removeItem(USER_ID_KEY);
  } catch (e) {
    console.warn('Failed to clear user ID:', e);
  }
}

/**
 * Get user ID age in days
 */
export function getUserIdAge() {
  try {
    const stored = localStorage.getItem(USER_ID_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      const createdAt = new Date(data.createdAt);
      const now = new Date();
      const diffTime = Math.abs(now - createdAt);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  } catch (e) {
    return 0;
  }
}

